import ENV from "glesys-controlpanel/config/environment";
import { isNone } from "@ember/utils";
import { getOwner } from "@ember/owner";

export const getOrigin = (url) => new URL(url, document.location.origin).origin;

export const ioApiBaseUri = ENV.ioApiBaseUri || document.location.origin;

export const isApiDirectlyUri = (url) => getOrigin(url) === getOrigin(ENV.apiBaseUri);

const isApiUri = (url) => getOrigin(url) === getOrigin(ioApiBaseUri) || isApiDirectlyUri(url);

export const WithDefaultHeaders = {
  async request({ request }, next) {
    let headers = new Headers(request.headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return next(Object.assign({}, request, { headers }));
  },
};

const buildAuthRequest =
  (session) =>
  async ({ request }, next) => {
    const sessionService = session ?? getOwner(request.store).lookup("service:session");
    const headers = new Headers(request.headers);

    if (sessionService.basicToken && isApiUri(request.url)) {
      const context = request.options?.context;
      const token = context ? sessionService.tokenFor(context) : sessionService.basicToken;
      headers.set("Authorization", `Basic ${token}`);
    }

    return next(Object.assign({}, request, { headers }));
  };

export const WithAuthToken = (session) => ({ request: buildAuthRequest(session) });
export const WithStoreAuthToken = { request: buildAuthRequest() };

const patchLists = ({ store, options, records, op }) => {
  const patchOp = { createRecord: "add", deleteRecord: "remove" }[op];

  if (!patchOp || !store || !Array.isArray(options?.listsToPatch) || !records?.length) {
    return;
  }

  options.listsToPatch.forEach((listQuery) => {
    const listKey = store.cacheKeyManager.getOrCreateDocumentIdentifier(listQuery);
    if (!listKey || !store.cache.peekRequest(listKey)) {
      return;
    }

    store.cache.patch(records.map((record) => ({ op: patchOp, record: listKey, field: "data", value: record })));
    store.notifications.notify(listKey, "updated");
  });
};

// Patches created and deleted records into the cached list queries `options.listsToPatch`
// names, so the UI updates without refetching the list. Deletes need it too, the UI drops
// them on its own but the cache keeps them, so the next patch would bring them back.
export const CachePatchHandler = {
  async request(context, next) {
    const response = await next(context.request);
    patchLists(context.request);

    return response;
  },
};

export const toJsonApiResource = (record, handler) => {
  const { type, primaryKey = "id", relationships: relationshipConfigs = {}, exclude = [] } = handler;
  const { [primaryKey]: id, ...attributes } = record;

  const relationshipConfigEntries = Object.entries(relationshipConfigs);

  const relationships = Object.fromEntries(
    relationshipConfigEntries.map(([name, config]) => {
      const foreignId = config.id ?? attributes[config.key];
      const data = isNone(foreignId) ? null : { type: config.type, id: String(foreignId) };
      return [name, { data }];
    }),
  );

  const relationshipAttributes = relationshipConfigEntries
    .filter(([, config]) => isNone(config.id) && config.key)
    .map(([, config]) => config.key);

  for (const key of [...relationshipAttributes, ...exclude]) {
    delete attributes[key];
  }

  const resource = { type, id: String(id), attributes };

  if (Object.keys(relationships).length) {
    resource.relationships = relationships;
  }

  return resource;
};

export const GlesysApiHandler = {
  async request(context, next) {
    const handler = context.request.options?.handler;

    let structuredDocument;

    try {
      structuredDocument = await next(context.request);
    } catch (error) {
      if (error.isRequestError && error.status >= 400 && isApiUri(context.request.url)) {
        error.content = {
          ...error.content,
          errors: [
            {
              status: String(error.status),
              title: error.statusText,
              detail: error.content?.response?.status?.text ?? error.content?.error?.message,
            },
          ],
        };
      }

      throw error;
    }

    if (!handler) {
      return isApiUri(context.request.url) ? structuredDocument.content.response : structuredDocument;
    }

    const payload = structuredDocument.content.response;
    const { envelope } = handler;

    const raw = (envelope.collection && payload[envelope.collection]) ?? (envelope.single && payload[envelope.single]);

    if (!raw && context.request.op === "deleteRecord") {
      return structuredDocument;
    }

    const data = Array.isArray(raw)
      ? raw.map((item) => toJsonApiResource(item, handler))
      : toJsonApiResource(raw, handler);

    return { data };
  },
};
